import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useEditAccessRequest } from "../components/EditAccessRequest";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/GradientCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import UserPickerDialog from "../components/UserPickerDialog";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import { tabAccessibleFor } from "../lib/modules";
import { toast } from "sonner";
import {
  Plus, Trash2, Award, TrendingUp, Target, Users, CheckCircle2, XCircle,
  CreditCard, Save, Calculator, Trophy, Inbox, Clock, Wallet, Search,
  ChevronRight, LayoutGrid, List, Percent, HandCoins, UserCog,
  ClipboardCheck, ShieldCheck, Eye, UserPlus, X,
} from "lucide-react";

const COMMISSION_TYPES = ["After GST Deduction", "On Total Order Value"];

const STAGE_LABEL = {
  manager_review: "Pending Assignment",
  manager_assigned: "Manager Assigned",
  admin_approval: "Pre-Approved",
  payment_pending: "Payment Pending",
  paid: "Paid",
  rejected: "Rejected",
};
const STAGE_TONE = {
  manager_review: "bg-slate-50 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-200 dark:ring-slate-700",
  manager_assigned: "bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30",
  admin_approval: "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/30",
  payment_pending: "bg-sky-50 text-sky-700 ring-1 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/30",
  paid: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30",
  rejected: "bg-rose-50 text-rose-700 ring-1 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30",
};

const INR = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtDateTime = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
};

function emptyForm() {
  return {
    // v107 — Auto/Manual mode + linked order metadata
    mode: "manual",
    order_id: "",
    order_no: "",
    client_name: "",
    completed_yearly_target: false,
    before_gst_amount: "",
    gst_percent: "18",
    commission_percent: "",
    commission_type: "After GST Deduction",
    incentive_demanded_percent: "",
    incentive_given_percent: "",
    helper_user_ids: [],
    helper_user_names: [],
    share_incentive: false,
    shared_with: [],
  };
}

function StageBadge({ stage, status, hasAssignedManager }) {
  // v106c — Pending Assignment vs Manager Assigned depends on whether
  // a reviewer has been picked; `hasAssignedManager` is the backend flag
  // that survives blind-review masking.
  let key = stage;
  if (stage === "manager_review" && hasAssignedManager) key = "manager_assigned";
  if (!key) key = status === "Rejected" ? "rejected" : "admin_approval";
  const label = STAGE_LABEL[key] || status || "Pending";
  return (
    <Badge className={`${STAGE_TONE[key] || "bg-slate-50 text-slate-700"} text-[10px] font-semibold`} data-keep-color>
      {label}
    </Badge>
  );
}

export default function Incentive() {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";

  const [tab, setTab] = useState("mine");
  const [viewMode] = useGlobalViewMode(); // v109 — synced with the top-bar toggle
  const [submitOpen, setSubmitOpen] = useState(false);
  const [viewing, setViewing] = useState(null); // detail dialog
  const [reviewing, setReviewing] = useState(null);
  const [adminActing, setAdminActing] = useState(null);
  const [paying, setPaying] = useState(null);
  const [reassigning, setReassigning] = useState(null);
  // v104 — MA can open the Tab Access picker directly from any of the
  // Review / Payment / Config tabs, without going to Target & Formula.
  const [quickPicker, setQuickPicker] = useState(null); // 'review' | 'payment' | 'config'
  const tabAccessChooserOpen = false; const setTabAccessChooserOpen = () => {};
  void tabAccessChooserOpen; void setTabAccessChooserOpen;

  const [mine, setMine] = useState([]);
  const [review, setReview] = useState([]);
  const [payment, setPayment] = useState([]);
  const [adminQueue, setAdminQueue] = useState([]);

  const [target, setTarget] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [people, setPeople] = useState([]);

  // Tab access derived from config.tab_access
  const uid = user?.id;
  const tabAccess = cfg?.tab_access || { review: [], payment: [], config: [], manager_incentive: [] };
  const isManagerIncentive = uid && (tabAccess.manager_incentive || []).includes(uid);
  const canReview = isMaster || isManagerIncentive || (uid && tabAccess.review?.includes(uid));
  const canPayment = isMaster || (uid && tabAccess.payment?.includes(uid));
  const canConfigWrite = isMaster || (uid && tabAccess.config?.includes(uid));
  const canConfigView = canConfigWrite;
  // Only Master Admin + Manager for Incentive users can assign/reassign a reviewer.
  const canAssign = isMaster || isManagerIncentive;

  const loadAll = async () => {
    try {
      const [tRes, cRes, pRes, mRes] = await Promise.all([
        api.get("/incentive/my-target").catch(() => ({ data: null })),
        api.get("/incentive/config"),
        api.get("/directory"),
        api.get("/incentive/submissions", { params: { scope: "mine" } }),
      ]);
      setTarget(tRes.data);
      setCfg(cRes.data);
      setPeople((pRes.data || []).filter((x) => x.id !== user.id));
      setMine(mRes.data || []);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    loadAll();
  }, [user?.id]);

  // Load review / payment / admin queues conditionally
  useEffect(() => {
    if (!cfg || !user) return;
    // v106 — always fetch: the backend already scopes correctly. This
    // way a plain employee assigned as reviewer will see the Review tab
    // light up without needing tab_access.review membership.
    api.get("/incentive/submissions", { params: { scope: "review" } })
      .then(({ data }) => setReview(data || []))
      .catch(() => setReview([]));
    if (canPayment) {
      api.get("/incentive/submissions", { params: { scope: "payment" } })
        .then(({ data }) => setPayment(data || []))
        .catch(() => setPayment([]));
    } else {
      setPayment([]);
    }
    if (isMaster) {
      api.get("/incentive/submissions", { params: { scope: "admin" } })
        .then(({ data }) => setAdminQueue(data || []))
        .catch(() => setAdminQueue([]));
    } else {
      setAdminQueue([]);
    }
  }, [cfg, user, canPayment, isMaster]);

  const reload = () => {
    loadAll();
    api.get("/incentive/submissions", { params: { scope: "review" } }).then(({ data }) => setReview(data || [])).catch(() => {});
    if (canPayment) api.get("/incentive/submissions", { params: { scope: "payment" } }).then(({ data }) => setPayment(data || [])).catch(() => {});
    if (isMaster) api.get("/incentive/submissions", { params: { scope: "admin" } }).then(({ data }) => setAdminQueue(data || [])).catch(() => {});
  };

  const mineStats = useMemo(() => {
    const paidTotal = mine.filter((s) => s.status === "Paid").reduce((sum, s) => sum + (s.incentive_amount || 0), 0);
    const pendingTotal = mine.filter((s) => s.stage && s.stage !== "paid" && s.stage !== "rejected")
      .reduce((sum, s) => sum + (s.incentive_amount || 0), 0);
    return { paidTotal, pendingTotal };
  }, [mine]);

  const reviewStats = useMemo(() => {
    const awaitAmt = review.reduce((sum, s) => sum + (s.incentive_amount || 0), 0);
    return { pending: review.length, amount: awaitAmt };
  }, [review]);

  const payStats = useMemo(() => {
    const toDisburse = payment.filter((s) => s.stage === "payment_pending");
    const paid = payment.filter((s) => s.stage === "paid");
    return {
      toDisburse: toDisburse.length,
      toDisburseAmt: toDisburse.reduce((s, x) => s + (x.incentive_amount || 0), 0),
      paidCount: paid.length,
      paidAmt: paid.reduce((s, x) => s + (x.incentive_amount || 0), 0),
    };
  }, [payment]);

  // v106 — Reviewer pool = every active employee. Temporary accounts
  // are excluded (they're filtered server-side via `role` check).
  const reviewerOptions = useMemo(() => {
    if (!cfg) return [];
    return (cfg.all_employees || []).filter(
      (u) => u.role !== "candidate_temp" && u.role !== "ex_employee_temp"
    );
  }, [cfg]);

  return (
    <div className="max-w-[1500px] space-y-5" data-testid="incentive-module">
      <PageHeader
        moduleKey="incentive"
        tone="indigo"
        icon={Award}
        eyebrow="Finance · Performance"
        title="Incentive"
        subtitle="Submit, review, disburse and configure — all your incentive activity in one place."
        actions={null}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList
          className="w-full h-auto flex-wrap justify-start gap-1 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-1 rounded-lg"
          data-testid="inc-tabs-list"
        >
          {/* v109.9.66 — Universal Law: Master Access is the sole controller
              for tab visibility. Master admin always sees all via `isMaster`. */}
          <TabTrigger value="mine" testid="inc-tab-mine" accent="indigo" count={mine.length} show={tabAccessibleFor(user, "incentive", "mine")}>My Submissions</TabTrigger>
          <TabTrigger value="review" testid="inc-tab-review" accent="amber" count={review.length} show={tabAccessibleFor(user, "incentive", "review")}>Review By Manager</TabTrigger>
          <TabTrigger value="payment" testid="inc-tab-payment" accent="sky" count={payment.length} show={tabAccessibleFor(user, "incentive", "payment")}>Amount Disbursed</TabTrigger>
          <TabTrigger value="config" testid="inc-tab-config" accent="slate" show={tabAccessibleFor(user, "incentive", "config")}>Target &amp; Formula</TabTrigger>
        </TabsList>

        {/* Tab 1: My Submissions */}
        <TabsContent value="mine" className="mt-4 space-y-4">
          <StatStrip
            items={[
              { label: "Yearly Target", value: INR(target?.yearly_target), icon: Target, accent: "indigo" },
              {
                label: `Sales Done${target?.fy_label ? ` · ${target.fy_label}` : ""}`,
                value: INR(target?.fy_sales_done),
                subtitle: target?.fy_target_completion_pct != null ? `${target.fy_target_completion_pct}% of target` : "",
                icon: TrendingUp, accent: "emerald",
              },
              { label: "My Submissions", value: mine.length, icon: Inbox, accent: "slate" },
              { label: "Total Paid Out", value: INR(mineStats.paidTotal), icon: Wallet, accent: "emerald" },
              { label: "Pending Approval", value: INR(mineStats.pendingTotal), icon: Clock, accent: "amber" },
            ]}
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[12px] text-slate-500 dark:text-slate-400">
              {mine.length} submission{mine.length === 1 ? "" : "s"}
            </div>
            <Button
              onClick={() => setSubmitOpen(true)}
              className="!bg-indigo-600 hover:!bg-indigo-700 !text-white shadow-sm"
              data-testid="inc-open-submit"
              data-keep-color
            >
              <Plus size={14} className="mr-1.5" /> Submit Incentive Request
            </Button>
          </div>
          {mine.length === 0 ? (
            <EmptyState title="No submissions yet" hint="Click 'Submit Incentive Request' to create your first one." />
          ) : viewMode === "card" ? (
            <MineCards submissions={mine} userId={user?.id} onView={setViewing} />
          ) : (
            <MineTable submissions={mine} userId={user?.id} onView={setViewing} />
          )}
        </TabsContent>

        {/* Tab 2: Review By Manager */}
        {tabAccessibleFor(user, "incentive", "review") && (
          <TabsContent value="review" className="mt-4 space-y-4">
            <StatStrip
              items={[
                { label: "Awaiting Your Review", value: reviewStats.pending, icon: Clock, accent: "amber" },
                { label: "Total Amount at Stake", value: INR(reviewStats.amount), icon: HandCoins, accent: "indigo" },
                { label: "Team Members", value: new Set(review.map((r) => r.created_by_id)).size, icon: Users, accent: "slate" },
              ]}
            />
            <div className="text-[11px] text-slate-500 dark:text-slate-400 italic">
              {(isMaster || isManagerIncentive)
                ? "Master Admin & Manager for Incentive see every request. Assign a Reviewer from the Reviewer Pool — that person will see only the anonymised request and their access closes once they decide."
                : "You have temporary access to a request assigned to you for review. Client name and submitter identity are hidden — decide based on the numbers alone."}
            </div>
            {review.length === 0 ? (
              <EmptyState title="Nothing to review" hint="Requests you can act on will show up here." />
            ) : viewMode === "card" ? (
              <ReviewCards submissions={review} currentUserId={uid} canAssign={canAssign} canReviewAny={isMaster || isManagerIncentive} onReview={setReviewing} onView={setViewing} onReassign={setReassigning} />
            ) : (
              <ReviewTable submissions={review} currentUserId={uid} canAssign={canAssign} canReviewAny={isMaster || isManagerIncentive} onReview={setReviewing} onView={setViewing} onReassign={setReassigning} />
            )}
          </TabsContent>
        )}

        {/* Tab 3: Amount Disbursed */}
        {tabAccessibleFor(user, "incentive", "payment") && (
          <TabsContent value="payment" className="mt-4 space-y-4">
            <StatStrip
              items={[
                { label: "To Disburse", value: payStats.toDisburse, icon: Clock, accent: "amber" },
                { label: "Pending Amount", value: INR(payStats.toDisburseAmt), icon: HandCoins, accent: "sky" },
                { label: "Paid Out", value: payStats.paidCount, icon: CheckCircle2, accent: "emerald" },
                { label: "Paid Amount", value: INR(payStats.paidAmt), icon: Wallet, accent: "emerald" },
              ]}
            />
            {isMaster && adminQueue.length > 0 && (
              <div className="rounded-lg border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50/40 dark:bg-indigo-500/5 p-3" data-testid="inc-admin-queue">
                <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-indigo-700 dark:text-indigo-300 mb-2">Master Admin · Awaiting Final Approval ({adminQueue.length})</div>
                <AdminApprovalTable submissions={adminQueue} onAct={setAdminActing} onView={setViewing} />
              </div>
            )}
            {payment.length === 0 ? (
              <EmptyState title="No disbursements queued" hint="Submissions approved by Master Admin will appear here." />
            ) : viewMode === "card" ? (
              <PaymentCards submissions={payment} onMarkPaid={setPaying} onView={setViewing} canMark={canPayment} />
            ) : (
              <PaymentTable submissions={payment} onMarkPaid={setPaying} onView={setViewing} canMark={canPayment} />
            )}
          </TabsContent>
        )}

        {/* Tab 4: Target & Formula */}
        {tabAccessibleFor(user, "incentive", "config") && (
          <TabsContent value="config" className="mt-4 space-y-5">
            {cfg && (
              <StatStrip
                items={[
                  { label: "Multiplier", value: cfg.multiplier ?? 0, icon: Percent, accent: "indigo" },
                  { label: "Manager for Incentive", value: (tabAccess.manager_incentive || []).length, icon: UserCog, accent: "indigo" },
                  { label: "Payment Desk", value: (tabAccess.payment || []).length, icon: Wallet, accent: "sky" },
                  { label: "Reviewers Pool", value: (tabAccess.review || []).length, icon: ClipboardCheck, accent: "amber" },
                  { label: "Config Editors", value: (tabAccess.config || []).length, icon: ShieldCheck, accent: "slate" },
                ]}
              />
            )}
            {isMaster && (
              <div className="hidden" />
            )}
            <TargetFormulaPanel cfg={cfg} isMaster={isMaster} canEdit={canConfigWrite} onSaved={reload} />
          </TabsContent>
        )}
      </Tabs>

      {submitOpen && (
        <SubmitDialog
          people={people}
          target={target}
          onClose={() => setSubmitOpen(false)}
          onSaved={() => { setSubmitOpen(false); reload(); setTab("mine"); }}
        />
      )}
      {viewing && (
        <ViewSubmissionDialog submission={viewing} onClose={() => setViewing(null)} />
      )}
      {reviewing && (
        <ManagerDecisionDialog
          submission={reviewing}
          onClose={() => setReviewing(null)}
          onSaved={() => { setReviewing(null); reload(); }}
        />
      )}
      {adminActing && (
        <AdminDecisionDialog
          submission={adminActing}
          onClose={() => setAdminActing(null)}
          onSaved={() => { setAdminActing(null); reload(); }}
        />
      )}
      {paying && (
        <MarkPaidDialog
          submission={paying}
          onClose={() => setPaying(null)}
          onSaved={() => { setPaying(null); reload(); }}
        />
      )}
      {reassigning && (
        <ReassignManagerDialog
          submission={reassigning}
          reviewerOptions={reviewerOptions}
          onClose={() => setReassigning(null)}
          onSaved={() => { setReassigning(null); reload(); }}
        />
      )}
      {quickPicker && (isMaster || (uid && tabAccess.review?.includes(uid))) && cfg && (
        <TabAccessPickerDialog
          which={quickPicker}
          all={cfg.all_employees || []}
          selectedIds={tabAccess[quickPicker] || []}
          onClose={() => setQuickPicker(null)}
          onSave={async (ids) => {
            try {
              const nextTA = { ...tabAccess, [quickPicker]: ids };
              await api.patch("/incentive/config", { tab_access: nextTA });
              toast.success("Tab access updated");
              setQuickPicker(null);
              reload();
            } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
          }}
        />
      )}
      {/* v109.9.40 — TabAccessManagerDialog removed per user request */}
    </div>
  );
}

/* ==== Small primitives ==== */

function ViewToggleBtn({ active, onClick, testid, children, borderLeft = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-[11.5px] font-semibold transition-colors ${borderLeft ? "border-l border-indigo-200 dark:border-indigo-500/40" : ""} ${active ? "!bg-indigo-600 !text-white" : "text-indigo-700 dark:text-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-500/10"}`}
      data-testid={testid}
      data-keep-color={active ? "" : undefined}
    >
      {children}
    </button>
  );
}

const TAB_ACCENT = {
  indigo: "data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 data-[state=active]:ring-1 data-[state=active]:ring-indigo-200 dark:data-[state=active]:bg-indigo-500/10 dark:data-[state=active]:text-indigo-200 dark:data-[state=active]:ring-indigo-500/30",
  amber: "data-[state=active]:bg-amber-50 data-[state=active]:text-amber-700 data-[state=active]:ring-1 data-[state=active]:ring-amber-200 dark:data-[state=active]:bg-amber-500/10 dark:data-[state=active]:text-amber-200 dark:data-[state=active]:ring-amber-500/30",
  sky: "data-[state=active]:bg-sky-50 data-[state=active]:text-sky-700 data-[state=active]:ring-1 data-[state=active]:ring-sky-200 dark:data-[state=active]:bg-sky-500/10 dark:data-[state=active]:text-sky-200 dark:data-[state=active]:ring-sky-500/30",
  slate: "data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:ring-1 data-[state=active]:ring-slate-300 dark:data-[state=active]:bg-slate-800 dark:data-[state=active]:text-slate-100 dark:data-[state=active]:ring-slate-700",
};

function TabTrigger({ value, children, testid, accent = "indigo", count, show = true }) {
  if (!show) return null;
  return (
    <TabsTrigger
      value={value}
      data-testid={testid}
      className={`text-[12.5px] font-semibold px-3 py-2 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 transition-colors rounded-md ${TAB_ACCENT[accent] || TAB_ACCENT.indigo}`}
    >
      {children}
      {count !== undefined && count !== null && (
        <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200">{count}</span>
      )}
    </TabsTrigger>
  );
}

const STAT_ACCENT = {
  indigo: { icon: "text-indigo-600 dark:text-indigo-300", ring: "ring-indigo-100 dark:ring-indigo-500/30", chip: "bg-indigo-50 dark:bg-indigo-500/10" },
  emerald: { icon: "text-emerald-600 dark:text-emerald-300", ring: "ring-emerald-100 dark:ring-emerald-500/30", chip: "bg-emerald-50 dark:bg-emerald-500/10" },
  amber: { icon: "text-amber-600 dark:text-amber-300", ring: "ring-amber-100 dark:ring-amber-500/30", chip: "bg-amber-50 dark:bg-amber-500/10" },
  sky: { icon: "text-sky-600 dark:text-sky-300", ring: "ring-sky-100 dark:ring-sky-500/30", chip: "bg-sky-50 dark:bg-sky-500/10" },
  slate: { icon: "text-slate-600 dark:text-slate-300", ring: "ring-slate-200 dark:ring-slate-700", chip: "bg-slate-100 dark:bg-slate-800" },
};

function StatStrip({ items }) {
  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(180px, 1fr))` }} data-testid="inc-stats">
      {items.map((it, i) => {
        const t = STAT_ACCENT[it.accent] || STAT_ACCENT.slate;
        const Icon = it.icon || TrendingUp;
        return (
          <div key={i} className="relative rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-3.5 flex items-start gap-3 hover:shadow-sm transition-shadow" data-testid={`inc-stat-${i}`}>
            <div className={`w-9 h-9 rounded-md ${t.chip} ring-1 ${t.ring} flex items-center justify-center flex-shrink-0`}>
              <Icon size={16} className={t.icon} strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 dark:text-slate-400 truncate">{it.label}</div>
              <div className="text-lg font-extrabold mono text-slate-900 dark:text-slate-100 truncate mt-0.5">{it.value}</div>
              {it.subtitle && <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 mt-0.5 truncate">{it.subtitle}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ==== My Submissions views ==== */

function MineCards({ submissions, userId, onView }) {
  return (
    <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(380px, 1fr))` }} data-testid="inc-mine-cards">
      {submissions.map((s) => (
        <SubmissionCard key={s.id} s={s} userId={userId} onView={onView} testid={`inc-mine-card-${s.id}`} />
      ))}
    </div>
  );
}

function SubmissionCard({ s, userId, onView, testid, extraFooter = null }) {
  const isOwn = s.created_by_id === userId;
  const isHelper = s.helper_user_ids?.includes(userId);
  const sharedRow = s.shared_with?.find((x) => x.user_id === userId);
  const incentiveDisplay = (s.stage === "manager_review" || s.stage === "admin_approval") ? "Pending" : INR(s.incentive_amount);
  return (
    <button
      type="button"
      onClick={() => onView && onView(s)}
      className="text-left rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5 hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-500/50 transition-all cursor-pointer group"
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-bold text-slate-900 dark:text-slate-100 truncate">{s.client_name}</div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 mono truncate mt-0.5">by {s.created_by_name} · {fmtDateTime(s.created_at)}</div>
        </div>
        <StageBadge stage={s.stage} status={s.status} hasAssignedManager={s.has_assigned_manager} />
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <MiniStat label="Order" value={INR(s.total_order_value)} />
        <MiniStat label="Commission" value={INR(s.commission_amount)} />
        <MiniStat label="Incentive" value={incentiveDisplay} highlight />
      </div>
      <div className="flex items-center gap-1.5 flex-wrap min-h-[22px]">
        {isOwn && <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 text-[10px]" data-keep-color>Submitter</Badge>}
        {isHelper && <Badge className="bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300 text-[10px]" data-keep-color>Helper</Badge>}
        {sharedRow && <Badge className="bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300 text-[10px]" data-keep-color>Share {sharedRow.percent}%</Badge>}
        {s.assigned_manager_name && s.stage === "manager_review" && (
          <Badge className="bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300 text-[10px]" data-keep-color>Reviewer: {s.assigned_manager_name}</Badge>
        )}
        <span className="ml-auto text-[11px] text-indigo-600 dark:text-indigo-300 font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
          <Eye size={11} className="inline mr-1" /> View details
        </span>
      </div>
      {(s.manager_reason || s.admin_reason) && s.stage === "rejected" && (
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 text-[11px] italic text-rose-600 dark:text-rose-400">
          {s.manager_reason && <div>Manager: “{s.manager_reason}”</div>}
          {s.admin_reason && <div>Admin: “{s.admin_reason}”</div>}
        </div>
      )}
      {extraFooter && (
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
          {extraFooter}
        </div>
      )}
    </button>
  );
}

function MineTable({ submissions, userId, onView }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-x-auto">
      <table className="w-full text-sm responsive-table">
        <thead className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
          <tr>
            <Th>Client</Th>
            <Th>Order</Th>
            <Th>Commission</Th>
            <Th>Incentive</Th>
            <Th>Stage</Th>
            <Th>Submitted</Th>
          </tr>
        </thead>
        <tbody>
          {submissions.map((s) => {
            const isOwn = s.created_by_id === userId;
            return (
              <tr key={s.id} onClick={() => onView && onView(s)} className="border-b border-slate-100 dark:border-slate-800 last:border-b-0 hover:bg-slate-50/60 dark:hover:bg-slate-900/40 cursor-pointer" data-testid={`inc-mine-${s.id}`}>
                <Td data-label="Client">
                  <div className="font-semibold text-slate-900 dark:text-slate-100">{s.client_name}</div>
                  {!isOwn && <div className="text-[10px] text-slate-500 dark:text-slate-400 mono">by {s.created_by_name}</div>}
                </Td>
                <Td mono data-label="Order">{INR(s.total_order_value)}</Td>
                <Td mono data-label="Commission">{INR(s.commission_amount)}</Td>
                <Td mono data-label="Incentive"><span className="font-bold text-slate-900 dark:text-slate-100">{(s.stage === "manager_review" || s.stage === "admin_approval") ? "Pending" : INR(s.incentive_amount)}</span></Td>
                <Td data-label="Stage">
                  <StageBadge stage={s.stage} status={s.status} hasAssignedManager={s.has_assigned_manager} />
                  {s.manager_reason && s.stage === "rejected" && <div className="text-[10px] text-rose-600 dark:text-rose-400 mt-1 italic">Manager: “{s.manager_reason}”</div>}
                  {s.admin_reason && s.stage === "rejected" && <div className="text-[10px] text-rose-600 dark:text-rose-400 mt-1 italic">Admin: “{s.admin_reason}”</div>}
                </Td>
                <Td mono className="text-[11px] text-slate-500 dark:text-slate-400" data-label="Submitted">{fmtDateTime(s.created_at)}</Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ==== Review views ==== */

function ReviewCards({ submissions, currentUserId, canAssign, canReviewAny, onReview, onView, onReassign }) {
  return (
    <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(380px, 1fr))` }} data-testid="inc-review-cards">
      {submissions.map((s) => {
        const isMine = s.assigned_manager_id === currentUserId;
        const isUnassigned = !s.assigned_manager_id;
        const isOwn = s.created_by_id === currentUserId; // masked-out for temp reviewers
        const canDecide = canReviewAny ? !isOwn : isMine;
        return (
          <SubmissionCard
            key={s.id}
            s={s}
            onView={onView}
            testid={`inc-review-${s.id}`}
            extraFooter={
              isUnassigned ? (
                canAssign ? (
                  <Button size="sm" onClick={() => onReassign(s)} className="h-8 text-xs !bg-indigo-600 hover:!bg-indigo-700 !text-white" data-testid={`inc-review-assign-${s.id}`} data-keep-color>
                    <UserCog size={12} className="mr-1" /> Assign Reviewer
                  </Button>
                ) : (
                  <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 text-[10px]" data-keep-color>Awaiting reviewer</Badge>
                )
              ) : (
                <>
                  {canAssign && (
                    <Button size="sm" variant="outline" onClick={() => onReassign(s)} className="h-8 text-xs" data-testid={`inc-review-reassign-${s.id}`}>
                      <UserCog size={12} className="mr-1" /> Reassign
                    </Button>
                  )}
                  {canDecide && (
                    <Button size="sm" onClick={() => onReview(s)} className="h-8 text-xs !bg-amber-600 hover:!bg-amber-700 !text-white" data-testid={`inc-review-open-${s.id}`} data-keep-color>
                      <ClipboardCheck size={12} className="mr-1" /> Review
                    </Button>
                  )}
                </>
              )
            }
          />
        );
      })}
    </div>
  );
}

function ReviewTable({ submissions, currentUserId, canAssign, canReviewAny, onReview, onView, onReassign }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-x-auto">
      <table className="w-full text-sm responsive-table">
        <thead className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
          <tr>
            <Th>Submitted By</Th>
            <Th>Client</Th>
            <Th>Order</Th>
            <Th>Incentive Ask</Th>
            <Th>Assigned To</Th>
            <Th align="right">Action</Th>
          </tr>
        </thead>
        <tbody>
          {submissions.map((s) => {
            const isMine = s.assigned_manager_id === currentUserId;
            const isUnassigned = !s.assigned_manager_id;
            const isOwn = s.created_by_id === currentUserId;
            const canDecide = canReviewAny ? !isOwn : isMine;
            return (
              <tr key={s.id} className="border-b border-slate-100 dark:border-slate-800 last:border-b-0 hover:bg-slate-50/60 dark:hover:bg-slate-900/40" data-testid={`inc-review-row-${s.id}`}>
                <Td data-label="Submitted By" onClick={() => onView(s)} className="cursor-pointer">
                  <div className="font-semibold text-slate-900 dark:text-slate-100">{s.created_by_name}</div>
                  <div className="text-[10px] mono text-slate-500 dark:text-slate-400">{fmtDateTime(s.created_at)}</div>
                </Td>
                <Td onClick={() => onView(s)} className="cursor-pointer" data-label="Client">{s.client_name}</Td>
                <Td mono data-label="Order">{INR(s.total_order_value)}</Td>
                <Td mono data-label="Incentive Ask"><span className="font-bold text-slate-900 dark:text-slate-100">{INR(s.incentive_amount)}</span></Td>
                <Td data-label="Assigned To">
                  {isUnassigned ? (
                    <Badge className="bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300 text-[10px]" data-keep-color>Unassigned</Badge>
                  ) : (s.assigned_manager_name || <span className="text-slate-400 text-xs italic">Reviewer</span>)}
                </Td>
                <Td align="right" data-label="Action">
                  <div className="inline-flex gap-1.5">
                    {isUnassigned ? (
                      canAssign ? (
                        <Button size="sm" onClick={() => onReassign(s)} className="h-8 text-xs !bg-indigo-600 hover:!bg-indigo-700 !text-white" data-testid={`inc-review-assign-${s.id}`} data-keep-color>
                          <UserCog size={12} className="mr-1" /> Assign
                        </Button>
                      ) : (
                        <span className="text-[11px] italic text-slate-400 dark:text-slate-500">Awaiting reviewer</span>
                      )
                    ) : (
                      <>
                        {canAssign && (
                          <Button size="sm" variant="outline" onClick={() => onReassign(s)} className="h-8 text-xs" data-testid={`inc-review-reassign-${s.id}`}>
                            <UserCog size={12} className="mr-1" /> Reassign
                          </Button>
                        )}
                        {canDecide && (
                          <Button size="sm" onClick={() => onReview(s)} className="h-8 text-xs !bg-amber-600 hover:!bg-amber-700 !text-white" data-testid={`inc-review-open-${s.id}`} data-keep-color>
                            Review <ChevronRight size={12} className="ml-1" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ==== Admin queue ==== */

function AdminApprovalTable({ submissions, onAct, onView }) {
  return (
    <div className="rounded-md border border-indigo-200 dark:border-indigo-500/30 bg-white dark:bg-slate-950 overflow-x-auto">
      <table className="w-full text-sm responsive-table">
        <thead className="border-b border-indigo-200 dark:border-indigo-500/30 bg-indigo-50/60 dark:bg-indigo-500/10">
          <tr>
            <Th>Submitted By</Th><Th>Client</Th><Th>Incentive</Th><Th>Manager Note</Th><Th align="right">Decision</Th>
          </tr>
        </thead>
        <tbody>
          {submissions.map((s) => (
            <tr key={s.id} className="border-b border-indigo-100/60 dark:border-indigo-500/20 last:border-b-0 hover:bg-indigo-50/40 dark:hover:bg-indigo-500/5" data-testid={`inc-admin-${s.id}`}>
              <Td onClick={() => onView(s)} className="cursor-pointer" data-label="Submitted By">
                <div className="font-semibold text-slate-900 dark:text-slate-100">{s.created_by_name}</div>
                <div className="text-[10px] mono text-slate-500 dark:text-slate-400">{fmtDateTime(s.created_at)}</div>
              </Td>
              <Td onClick={() => onView(s)} className="cursor-pointer" data-label="Client">{s.client_name}</Td>
              <Td mono data-label="Incentive"><span className="font-bold text-slate-900 dark:text-slate-100">{INR(s.incentive_amount)}</span></Td>
              <Td data-label="Manager Note">
                <div className="text-[11.5px] text-slate-700 dark:text-slate-300">{s.manager_decided_by_name || "—"}</div>
                {s.manager_reason && <div className="text-[10px] text-slate-500 dark:text-slate-400 italic">“{s.manager_reason}”</div>}
              </Td>
              <Td align="right" data-label="Decision">
                <Button size="sm" onClick={() => onAct(s)} className="h-8 text-xs !bg-indigo-600 hover:!bg-indigo-700 !text-white" data-testid={`inc-admin-open-${s.id}`} data-keep-color>
                  Approve / Reject
                </Button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ==== Payment views ==== */

function PaymentCards({ submissions, onMarkPaid, onView, canMark }) {
  return (
    <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(380px, 1fr))` }} data-testid="inc-pay-cards">
      {submissions.map((s) => (
        <SubmissionCard
          key={s.id}
          s={s}
          onView={onView}
          testid={`inc-pay-${s.id}`}
          extraFooter={
            canMark && (
              <Button size="sm" onClick={() => onMarkPaid(s)} className="h-8 text-xs !bg-sky-600 hover:!bg-sky-700 !text-white" data-testid={`inc-pay-open-${s.id}`} data-keep-color>
                <CreditCard size={12} className="mr-1" /> {s.stage === "paid" ? "Update Payment" : "Record Payment"}
              </Button>
            )
          }
        />
      ))}
    </div>
  );
}

function PaymentTable({ submissions, onMarkPaid, onView, canMark }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-x-auto">
      <table className="w-full text-sm responsive-table">
        <thead className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
          <tr>
            <Th>Beneficiary</Th><Th>Client</Th><Th>Incentive</Th><Th>Payment</Th><Th>Stage</Th><Th align="right">Action</Th>
          </tr>
        </thead>
        <tbody>
          {submissions.map((s) => (
            <tr key={s.id} className="border-b border-slate-100 dark:border-slate-800 last:border-b-0 hover:bg-slate-50/60 dark:hover:bg-slate-900/40" data-testid={`inc-pay-row-${s.id}`}>
              <Td onClick={() => onView(s)} className="cursor-pointer" data-label="Beneficiary">
                <div className="font-semibold text-slate-900 dark:text-slate-100">{s.created_by_name}</div>
                <div className="text-[10px] mono text-slate-500 dark:text-slate-400">{fmtDateTime(s.created_at)}</div>
              </Td>
              <Td onClick={() => onView(s)} className="cursor-pointer" data-label="Client">{s.client_name}</Td>
              <Td mono data-label="Incentive"><span className="font-bold text-slate-900 dark:text-slate-100">{INR(s.incentive_amount)}</span></Td>
              <Td data-label="Payment">
                {s.stage === "paid" ? (
                  <div className="text-[11.5px] text-slate-700 dark:text-slate-300">
                    <div><span className="font-semibold">{s.payment_mode || "—"}</span> {s.payment_reference && <span className="mono text-[10px]">· {s.payment_reference}</span>}</div>
                    <div className="text-[10px] text-slate-500 dark:text-slate-400">by {s.payment_disbursed_by_name} on {s.payment_disbursed_on}</div>
                  </div>
                ) : <span className="text-slate-400 text-xs italic">Pending</span>}
              </Td>
              <Td data-label="Stage"><StageBadge stage={s.stage} status={s.status} hasAssignedManager={s.has_assigned_manager} /></Td>
              <Td align="right" data-label="Action">
                {canMark && (
                  <Button size="sm" onClick={() => onMarkPaid(s)} className="h-8 text-xs !bg-sky-600 hover:!bg-sky-700 !text-white" data-testid={`inc-pay-open-${s.id}`} data-keep-color>
                    <CreditCard size={12} className="mr-1" /> {s.stage === "paid" ? "Update" : "Mark Paid"}
                  </Button>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ==== Target & Formula panel ==== */

function TargetFormulaPanel({ cfg, isMaster, canEdit, onSaved }) {
  // v109.9.9 — Save-and-request for incentive config edits.
  const { dialog: incReqDialog, wrapMutation: wrapIncReq } = useEditAccessRequest();
  const [multInput, setMultInput] = useState("");
  const [overrides, setOverrides] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!cfg) return;
    setMultInput(String(cfg.multiplier ?? 5));
    setOverrides(cfg.employee_targets || {});
  }, [cfg]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        multiplier: parseFloat(multInput) || 0,
        employee_targets: overrides,
      };
      const result = await wrapIncReq(
        () => api.patch("/incentive/config", payload),
        {
          moduleKey: "incentive_config",
          recordId: cfg?.id || "singleton",
          recordLabel: "Incentive Configuration",
          proposedPayload: payload,
          stageOn403: true,
        },
      );
      if (!result) { setSaving(false); return; }
      toast.success("Configuration saved");
      onSaved && onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setSaving(false);
  };

  if (!cfg) return <div className="text-sm text-slate-500 dark:text-slate-400">Loading configuration…</div>;

  return (
    <>
      {/* Yearly formula */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5" data-testid="inc-cfg-formula">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-md bg-indigo-50 dark:bg-indigo-500/10 ring-1 ring-indigo-100 dark:ring-indigo-500/30 flex items-center justify-center">
            <Calculator size={16} className="text-indigo-600 dark:text-indigo-300" strokeWidth={2} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 dark:text-slate-400">Yearly Target Formula</div>
            <div className="text-[13px] font-bold text-slate-900 dark:text-slate-100">monthly_salary × 12 × multiplier</div>
          </div>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 max-w-xs min-w-[140px]">
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Multiplier</Label>
            <Input type="number" step="0.1" value={multInput} onChange={(e) => setMultInput(e.target.value)} disabled={!canEdit} className="mt-1.5" data-testid="inc-cfg-multiplier" />
          </div>
          {canEdit && (
            <Button onClick={save} disabled={saving} className="!bg-indigo-600 hover:!bg-indigo-700 !text-white h-10" data-testid="inc-cfg-save" data-keep-color>
              <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save Configuration"}
            </Button>
          )}
        </div>
        {cfg.updated_by && (
          <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-2 mono">Last updated {fmtDateTime(cfg.updated_at)} by {cfg.updated_by}</div>
        )}
      </div>

      {/* Per-employee overrides */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-x-auto" data-testid="inc-cfg-employees">
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-emerald-50 dark:bg-emerald-500/10 ring-1 ring-emerald-100 dark:ring-emerald-500/30 flex items-center justify-center">
            <Trophy size={14} className="text-emerald-600 dark:text-emerald-300" strokeWidth={2} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 dark:text-slate-400">Per-Employee Targets</div>
            <div className="text-[13px] font-bold text-slate-900 dark:text-slate-100">Set an override (₹) to bypass the formula</div>
          </div>
        </div>
        <table className="w-full text-sm responsive-table">
          <thead className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
            <tr>
              <Th>Employee</Th><Th>Role</Th><Th>Monthly Salary</Th><Th>Computed Yearly Target</Th><Th>Override (₹)</Th>
            </tr>
          </thead>
          <tbody>
            {(cfg.eligible_users || []).length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-slate-400 dark:text-slate-500 text-sm">No eligible users found.</td></tr>
            )}
            {(cfg.eligible_users || []).map((u) => (
              <tr key={u.id} className="border-b border-slate-100 dark:border-slate-800 last:border-b-0 hover:bg-slate-50/60 dark:hover:bg-slate-900/40" data-testid={`inc-cfg-emp-${u.id}`}>
                <Td data-label="Employee">
                  <div className="font-semibold text-slate-900 dark:text-slate-100">{u.name}</div>
                  <div className="text-[10px] mono text-slate-500 dark:text-slate-400">{u.email}</div>
                </Td>
                <Td className="text-[12px]" data-label="Role">{u.role}</Td>
                <Td mono data-label="Monthly Salary">{INR(u.salary)}</Td>
                <Td mono data-label="Computed Target"><span className="font-bold text-slate-900 dark:text-slate-100">{INR(u.computed_target)}</span></Td>
                <Td data-label="Override">
                  <Input type="number" value={overrides[u.id] ?? ""} disabled={!canEdit} onChange={(e) => setOverrides({ ...overrides, [u.id]: e.target.value })} placeholder="use formula" className="h-9" data-testid={`inc-cfg-override-${u.id}`} />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canEdit && (
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="!bg-indigo-600 hover:!bg-indigo-700 !text-white" data-testid="inc-cfg-save-bottom" data-keep-color>
            <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save All Configuration"}
          </Button>
        </div>
      )}
      {/* v109.9.9 — Save-and-request dialog (auto-opens on 403) */}
      {incReqDialog}
    </>
  );
}

function TabAccessManagerDialog({ cfg, onClose, onSaved }) {
  const initial = {
    manager_incentive: cfg.tab_access?.manager_incentive || [],
    review: cfg.tab_access?.review || [],
    payment: cfg.tab_access?.payment || [],
    config: cfg.tab_access?.config || [],
  };
  const [tabAcc, setTabAcc] = useState(initial);
  const [extraIds, setExtraIds] = useState([]); // recently added users with no chip yet
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const allEmployees = cfg.all_employees || [];
  const idToUser = useMemo(() => {
    const m = new Map();
    for (const u of allEmployees) m.set(u.id, u);
    return m;
  }, [allEmployees]);

  const currentIds = useMemo(() => {
    const set = new Set([
      ...tabAcc.manager_incentive,
      ...tabAcc.review,
      ...tabAcc.payment,
      ...tabAcc.config,
      ...extraIds,
    ]);
    return Array.from(set);
  }, [tabAcc, extraIds]);

  const currentUsers = currentIds
    .map((id) => idToUser.get(id))
    .filter(Boolean)
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const toggleChip = (uid, key) => {
    setTabAcc((prev) => {
      const cur = new Set(prev[key]);
      if (cur.has(uid)) cur.delete(uid); else cur.add(uid);
      return { ...prev, [key]: Array.from(cur) };
    });
  };

  const removeUser = (uid) => {
    setTabAcc((prev) => ({
      manager_incentive: prev.manager_incentive.filter((x) => x !== uid),
      review: prev.review.filter((x) => x !== uid),
      payment: prev.payment.filter((x) => x !== uid),
      config: prev.config.filter((x) => x !== uid),
    }));
    setExtraIds((s) => s.filter((x) => x !== uid));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/incentive/config", { tab_access: tabAcc });
      toast.success("Tab access saved");
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const chipDefs = [
    { key: "manager_incentive", label: "Manager for Incentive", accent: "indigo" },
    { key: "review", label: "Review Pool", accent: "amber" },
    { key: "payment", label: "Amount Disbursed", accent: "sky" },
    { key: "config", label: "Target & Formula", accent: "emerald" },
  ];

  return (
    <>
      <Dialog open={!pickerOpen} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="inc-tab-access-manager">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 heading">
              <ShieldCheck className="w-5 h-5 text-indigo-600 dark:text-indigo-300" /> Incentive · Tab Access
            </DialogTitle>
            <DialogDescription>
              Add users, then click the tags on each user to grant access to specific tabs. New users start with no access by default.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 bg-slate-50/60 dark:bg-slate-900/40 space-y-3">
            <div className="text-[12px] uppercase tracking-wide text-slate-500 dark:text-slate-400 font-medium">Add users</div>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="w-full text-left rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 hover:border-indigo-400 dark:hover:border-indigo-500"
              data-testid="inc-tab-access-open-picker"
            >
              <span className="text-slate-500 dark:text-slate-400 text-[12.5px]">Click to pick employees…</span>
              <span className="float-right text-slate-400 dark:text-slate-500">▸ Pick</span>
            </button>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">
              Selected users appear below with zero access. Click any tag to toggle access to that tab.
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-800 mt-3">
            <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-[12px] uppercase tracking-wide text-slate-500 dark:text-slate-400 font-medium flex items-center justify-between">
              <span>Current users ({currentUsers.length})</span>
              <span className="normal-case text-[10.5px] text-slate-400 dark:text-slate-500">Click tag to grant / revoke</span>
            </div>
            <div className="max-h-[440px] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
              {currentUsers.length === 0 && (
                <div className="p-6 text-center text-slate-400 dark:text-slate-500 text-[13px]">No users added yet — click <span className="font-semibold">Add users</span> above.</div>
              )}
              {currentUsers.map((u) => (
                <div key={u.id} className="px-4 py-3 flex items-start justify-between gap-3" data-testid={`inc-tab-access-row-${u.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-[13.5px] font-semibold text-slate-900 dark:text-slate-100 truncate">{u.name}</div>
                      <span className="text-[10.5px] text-slate-500 dark:text-slate-400 mono">{u.role || ""}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {chipDefs.map((c) => (
                        <AccessChip
                          key={c.key}
                          label={c.label}
                          active={tabAcc[c.key].includes(u.id)}
                          onClick={() => toggleChip(u.id, c.key)}
                          accent={c.accent}
                          testid={`inc-chip-${c.key}-${u.id}`}
                        />
                      ))}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeUser(u.id)}
                    className="text-rose-500 hover:text-rose-700 flex-shrink-0 h-8 w-8 flex items-center justify-center"
                    title="Remove user from all tabs"
                    data-testid={`inc-tab-access-remove-${u.id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="!bg-indigo-600 hover:!bg-indigo-700 !text-white"
              data-keep-color
              data-testid="inc-tab-access-save"
            >
              <Save className="w-4 h-4 mr-1.5" />
              {saving ? "Saving…" : "Save Access"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UserPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title="Add users to Incentive Tab Access"
        users={allEmployees.filter((u) => u.role !== "candidate_temp" && u.role !== "ex_employee_temp")}
        initialSelectedIds={currentIds}
        onDone={(ids) => {
          const newOnes = ids.filter((id) => !currentIds.includes(id));
          if (newOnes.length > 0) setExtraIds((s) => Array.from(new Set([...s, ...newOnes])));
          setPickerOpen(false);
        }}
      />
    </>
  );
}

const CHIP_TONE = {
  indigo: { active: "!bg-indigo-600 !text-white !border-indigo-600", idle: "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:border-indigo-300 dark:hover:border-indigo-500/50" },
  amber:  { active: "!bg-amber-500 !text-white !border-amber-500",   idle: "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:border-amber-300 dark:hover:border-amber-500/50" },
  sky:    { active: "!bg-sky-600 !text-white !border-sky-600",       idle: "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:border-sky-300 dark:hover:border-sky-500/50" },
  emerald:{ active: "!bg-emerald-600 !text-white !border-emerald-600", idle: "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:border-emerald-300 dark:hover:border-emerald-500/50" },
};

function AccessChip({ label, active, onClick, accent = "indigo", testid }) {
  const t = CHIP_TONE[accent] || CHIP_TONE.indigo;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 text-[11px] font-semibold rounded-full border transition-colors ${active ? t.active : t.idle}`}
      data-testid={testid}
      data-keep-color={active ? "" : undefined}
    >
      {active && <CheckCircle2 size={10} className="inline mr-1 -mt-0.5" strokeWidth={2.5} />}
      {label}
    </button>
  );
}

function TabAccessPickerDialog({ which, all, selectedIds, onClose, onSave }) {
  const [picked, setPicked] = useState(() => new Set(selectedIds));  const [q, setQ] = useState("");
  const titleMap = {
    manager_incentive: "Manager for Incentive",
    review: "Review By Manager",
    payment: "Amount Disbursed",
    config: "Target & Formula",
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return all;
    const needle = q.toLowerCase();
    return all.filter((u) => (u.name || "").toLowerCase().includes(needle) || (u.role || "").toLowerCase().includes(needle));
  }, [q, all]);

  const toggle = (id) => {
    const s = new Set(picked);
    if (s.has(id)) s.delete(id); else s.add(id);
    setPicked(s);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`inc-picker-${which}`}>
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2">
            <ShieldCheck size={16} className="text-slate-700 dark:text-slate-200" /> {titleMap[which]} · Tab Access
          </DialogTitle>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">
            Selected users get access to the <span className="font-semibold">{titleMap[which]}</span> tab. Every employee retains view-only access to the module + full access to their own submissions.
          </div>
        </DialogHeader>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employees…" className="h-9 text-xs pl-8" data-testid={`inc-picker-search-${which}`} />
        </div>
        <div className="border border-slate-200 dark:border-slate-800 rounded-md max-h-[360px] overflow-y-auto">
          {filtered.length === 0 && <div className="text-center py-6 text-slate-400 dark:text-slate-500 text-sm">No employees match.</div>}
          {filtered.map((u) => {
            const active = picked.has(u.id);
            return (
              <label key={u.id} className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 dark:border-slate-800 last:border-b-0 hover:bg-slate-50/60 dark:hover:bg-slate-900/40 cursor-pointer" data-testid={`inc-picker-row-${which}-${u.id}`}>
                <input type="checkbox" checked={active} onChange={() => toggle(u.id)} />
                <div className="flex-1">
                  <div className="text-[13px] text-slate-900 dark:text-slate-100 font-semibold">{u.name}</div>
                  <div className="text-[10.5px] text-slate-500 dark:text-slate-400 mono">{u.role || ""}</div>
                </div>
                {active && <Badge className="bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300 text-[10px]" data-keep-color>Granted</Badge>}
              </label>
            );
          })}
        </div>
        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(Array.from(picked))} className="!bg-indigo-600 hover:!bg-indigo-700 !text-white" data-testid={`inc-picker-apply-${which}`} data-keep-color>
            Apply Selection ({picked.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==== Submit Dialog ==== */

function SubmitDialog({ people, target, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  // v107 — Auto mode state
  const [tgtStatus, setTgtStatus] = useState(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [eligibleOrders, setEligibleOrders] = useState([]);
  const [orderSearch, setOrderSearch] = useState("");
  const [autoOrder, setAutoOrder] = useState(null); // selected eligible order

  const isAuto = form.mode === "auto";
  const targetMet = !!tgtStatus?.target_met_at;

  // Load target-status + eligible orders once (used by Auto mode)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/incentive/eligible-orders");
        if (!alive) return;
        setTgtStatus(data?.target_status || null);
        setEligibleOrders(data?.orders || []);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  // Auto refresh orders when search changes
  useEffect(() => {
    if (!isAuto) return;
    setOrdersLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/incentive/eligible-orders", { params: { school: orderSearch } });
        setTgtStatus(data?.target_status || null);
        setEligibleOrders(data?.orders || []);
      } catch { /* silent */ }
      setOrdersLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [orderSearch, isAuto]);

  const setMode = (m) => {
    if (m === form.mode) return;
    if (m === "auto") {
      setForm((f) => ({ ...f, mode: "auto", order_id: "", order_no: "", client_name: "", before_gst_amount: "", gst_percent: "0" }));
      setAutoOrder(null);
    } else {
      setForm((f) => ({ ...f, mode: "manual", order_id: "", order_no: "", before_gst_amount: "", gst_percent: "18" }));
      setAutoOrder(null);
    }
  };

  const pickAutoOrder = (o) => {
    setAutoOrder(o);
    setForm((f) => ({
      ...f,
      order_id: o.order_id,
      order_no: o.order_no,
      client_name: o.client_name || f.client_name,
      before_gst_amount: String(o.before_gst_for_user || 0),
      gst_percent: "0", // Auto mode uses pre-GST value directly
      completed_yearly_target: true,
    }));
  };

  const calc = useMemo(() => {
    const before = parseFloat(form.before_gst_amount) || 0;
    const gst_pct = parseFloat(form.gst_percent) || 0;
    const gst_amount = +(before * gst_pct / 100).toFixed(2);
    const total = +(before + gst_amount).toFixed(2);
    const comm_pct = parseFloat(form.commission_percent) || 0;
    const base = form.commission_type === "After GST Deduction" ? before : total;
    const comm_amount = +(base * comm_pct / 100).toFixed(2);
    return { gst_amount, total, comm_amount };
  }, [form.before_gst_amount, form.gst_percent, form.commission_percent, form.commission_type]);

  const toggleHelper = (p) => {
    setForm((f) => {
      const has = f.helper_user_ids.includes(p.id);
      const newIds = has ? f.helper_user_ids.filter((x) => x !== p.id) : [...f.helper_user_ids, p.id];
      const newNames = has ? f.helper_user_names.filter((_, i) => f.helper_user_ids[i] !== p.id) : [...f.helper_user_names, p.name];
      let shared = f.shared_with;
      if (f.share_incentive) {
        if (has) shared = shared.filter((s) => s.user_id !== p.id);
        else if (!shared.some((s) => s.user_id === p.id)) shared = [...shared, { user_id: p.id, user_name: p.name, percent: "" }];
      }
      return { ...f, helper_user_ids: newIds, helper_user_names: newNames, shared_with: shared };
    });
  };

  const setShareIncentive = (v) => {
    setForm((f) => {
      if (!v) return { ...f, share_incentive: false, shared_with: [] };
      if (f.shared_with.length === 0 && f.helper_user_ids.length > 0) {
        const auto = f.helper_user_ids.map((uid, i) => ({ user_id: uid, user_name: f.helper_user_names[i] || "", percent: "" }));
        return { ...f, share_incentive: true, shared_with: auto };
      }
      return { ...f, share_incentive: true };
    });
  };

  const addShareRow = () => setForm((f) => ({ ...f, shared_with: [...f.shared_with, { user_id: "", user_name: "", percent: "" }] }));
  const updateShareRow = (i, patch) => setForm((f) => ({ ...f, shared_with: f.shared_with.map((s, ix) => ix === i ? { ...s, ...patch } : s) }));
  const removeShareRow = (i) => setForm((f) => ({ ...f, shared_with: f.shared_with.filter((_, ix) => ix !== i) }));

  const sharedSum = useMemo(() => form.shared_with.reduce((sum, s) => sum + (parseFloat(s.percent) || 0), 0), [form.shared_with]);

  const submit = async () => {
    if (!form.client_name.trim()) { toast.error("Client name is required"); return; }
    if (isAuto && !form.order_id) { toast.error("Pick an eligible order to continue in Auto mode"); return; }
    if (isAuto && !targetMet) { toast.error("You haven't met your Yearly Target yet — switch to Manual mode."); return; }
    if (form.share_incentive && sharedSum > 100) { toast.error("Total shared % cannot exceed 100"); return; }
    setSaving(true);
    try {
      await api.post("/incentive/submissions", { ...form, incentive_given_percent: 0 });
      toast.success("Incentive submitted");
      onSaved && onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0" data-testid="inc-submit-dialog">
        <div className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-white dark:bg-slate-950 ring-1 ring-slate-200 dark:ring-slate-800 flex items-center justify-center">
              <Award size={20} className="text-slate-700 dark:text-slate-200" />
            </div>
            <div>
              <DialogTitle className="heading text-base font-bold text-slate-900 dark:text-slate-100">Submit Incentive Request</DialogTitle>
              <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">A Manager for Incentive will be assigned by Master Admin (or anyone with Review-tab access) after you submit.</div>
            </div>
          </div>
        </div>
        <DialogHeader className="hidden"><DialogTitle>Submit</DialogTitle></DialogHeader>

        <div className="px-6 py-5 space-y-5">
          {/* v107 — Auto / Manual mode toggle + Yearly Target status */}
          <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">Submission Mode</div>
              <div className="inline-flex rounded-md ring-1 ring-slate-200 dark:ring-slate-800 overflow-hidden text-[12px]">
                <button type="button" onClick={() => setMode("auto")}
                  className={`px-3 py-1.5 font-semibold transition-colors ${isAuto ? "!bg-indigo-600 !text-white" : "bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200"}`}
                  data-testid="inc-mode-auto" data-keep-color={isAuto ? "" : undefined}>
                  Auto (from paid Orders)
                </button>
                <button type="button" onClick={() => setMode("manual")}
                  className={`px-3 py-1.5 font-semibold transition-colors ${!isAuto ? "!bg-indigo-600 !text-white" : "bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200"}`}
                  data-testid="inc-mode-manual" data-keep-color={!isAuto ? "" : undefined}>
                  Manual
                </button>
              </div>
            </div>
            {tgtStatus && (
              <div className={`rounded-md px-3 py-2 text-[11.5px] ${targetMet
                ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-200 ring-1 ring-emerald-200 dark:ring-emerald-500/30"
                : "bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-200 ring-1 ring-amber-200 dark:ring-amber-500/30"}`}
                data-testid="inc-target-banner">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="font-semibold flex items-center gap-1.5">
                    <Target size={13} strokeWidth={2.5} />
                    {targetMet
                      ? `Yearly Target met on ${tgtStatus.target_met_at}${tgtStatus.target_met_order_no ? ` (Order ${tgtStatus.target_met_order_no})` : ""}`
                      : "Yearly Target not yet met"}
                    <span className="text-slate-500 dark:text-slate-400 font-normal ml-1">· {tgtStatus.fy_label}</span>
                  </div>
                  <div className="mono font-semibold">{INR(tgtStatus.cumulative_paid_before_gst)} / {INR(tgtStatus.yearly_target)} <span className="text-slate-500 dark:text-slate-400 font-normal">({tgtStatus.target_completion_pct}%)</span></div>
                </div>
                {!targetMet && isAuto && (
                  <div className="mt-1 italic">Auto mode is locked until your target is met. Switch to <b>Manual</b> to submit a legacy incentive request.</div>
                )}
              </div>
            )}
          </div>

          {/* v107 — Auto mode: eligible orders picker */}
          {isAuto && targetMet && (
            <SectionCard title="Pick an eligible Order">
              <div className="flex items-center gap-2 mb-3">
                <Search size={14} className="text-slate-400" />
                <Input placeholder="Search by School / Client name…" value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)} className="h-9" data-testid="inc-order-search" />
              </div>
              {ordersLoading && <div className="text-[11.5px] text-slate-500 italic">Loading orders…</div>}
              {!ordersLoading && eligibleOrders.length === 0 && (
                <div className="text-[11.5px] text-slate-500 italic px-2 py-4 border border-dashed border-slate-300 dark:border-slate-700 rounded-md" data-testid="inc-no-orders">
                  No eligible orders found. Orders must be placed AFTER your target-met date, have every invoice marked Paid, and list you as an approved salesman.
                </div>
              )}
              {!ordersLoading && eligibleOrders.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1" data-testid="inc-order-list">
                  {eligibleOrders.map((o) => {
                    const active = autoOrder?.order_id === o.order_id;
                    return (
                      <button key={o.order_id} type="button" onClick={() => pickAutoOrder(o)}
                        className={`text-left px-3 py-2 rounded-md ring-1 transition-colors ${active ? "!bg-indigo-600 !text-white !ring-indigo-700" : "bg-white dark:bg-slate-950 ring-slate-200 dark:ring-slate-800 hover:ring-indigo-300 dark:hover:ring-indigo-500/40"}`}
                        data-testid={`inc-order-${o.order_no}`} data-keep-color={active ? "" : undefined}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-[12px] mono">{o.order_no}</span>
                          <span className="text-[10.5px] opacity-80">{o.order_date}</span>
                        </div>
                        <div className={`text-[11.5px] mt-0.5 ${active ? "text-white" : "text-slate-700 dark:text-slate-200"}`}>{o.client_name}</div>
                        <div className={`text-[10.5px] mt-0.5 mono ${active ? "text-white/80" : "text-slate-500 dark:text-slate-400"}`}>
                          Pre-GST (you): {INR(o.before_gst_for_user)} · Share {o.share_percent}% · {o.invoices_count} invoice{o.invoices_count === 1 ? "" : "s"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </SectionCard>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SoftField label="Client / School Name *">
              <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="Eg. ABC Public School" data-testid="inc-client" readOnly={isAuto && !!autoOrder} className={isAuto && !!autoOrder ? "bg-slate-50 dark:bg-slate-900" : ""} />
            </SoftField>
            <SoftField label="Did you complete your yearly target?">
              <div className="flex items-center gap-3 h-10 px-3 border border-slate-200 dark:border-slate-800 rounded-md bg-slate-50 dark:bg-slate-900">
                <Switch checked={form.completed_yearly_target} onCheckedChange={(v) => setForm({ ...form, completed_yearly_target: v })} data-testid="inc-completed" disabled={isAuto} />
                <span className="text-sm text-slate-700 dark:text-slate-300">{form.completed_yearly_target ? "Yes — completed" : "Not yet"}{isAuto ? " (auto)" : ""}</span>
              </div>
            </SoftField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SoftField label={`Yearly Target${target?.fy_label ? ` · ${target.fy_label}` : ""} (auto)`}>
              <Input value={INR(target?.yearly_target)} readOnly className="bg-slate-50 dark:bg-slate-900 mono font-semibold text-slate-800 dark:text-slate-200" />
            </SoftField>
            <SoftField label={`Sales Done${target?.fy_label ? ` · ${target.fy_label}` : ""} (auto)`}>
              <Input value={INR(target?.fy_sales_done)} readOnly className="bg-slate-50 dark:bg-slate-900 mono font-semibold text-slate-800 dark:text-slate-200" />
            </SoftField>
          </div>

          <SectionCard title={isAuto && autoOrder ? `Order Value — ${autoOrder.order_no}` : "Order Value"}>
            {isAuto && autoOrder && (
              <div className="mb-2 text-[11px] text-emerald-700 dark:text-emerald-300 italic">Pre-GST value auto-fetched from paid invoices linked to this order (your share).</div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <SoftField label="Before GST Amount (₹) *"><Input type="number" value={form.before_gst_amount} onChange={(e) => setForm({ ...form, before_gst_amount: e.target.value })} placeholder="0" data-testid="inc-before-gst" readOnly={isAuto && !!autoOrder} className={isAuto && !!autoOrder ? "bg-slate-50 dark:bg-slate-900 mono" : "mono"} /></SoftField>
              <SoftField label="GST %"><Input type="number" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} placeholder="18" data-testid="inc-gst-pct" readOnly={isAuto && !!autoOrder} className={isAuto && !!autoOrder ? "bg-slate-50 dark:bg-slate-900 mono" : "mono"} /></SoftField>
              <SoftField label="GST Amount (auto)"><Input value={INR(calc.gst_amount)} readOnly className="bg-slate-50 dark:bg-slate-900 mono" /></SoftField>
            </div>
            <div className="mt-3 px-4 py-2.5 border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 rounded-md flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{isAuto ? "Incentive Base Value (pre-GST)" : "Total Order Value (incl. GST)"}</span>
              <span className="text-base font-bold text-slate-900 dark:text-slate-100 mono" data-testid="inc-total-order">{INR(isAuto ? (parseFloat(form.before_gst_amount) || 0) : calc.total)}</span>
            </div>
          </SectionCard>

          <SectionCard title="Commission">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <SoftField label="Commission %"><Input type="number" value={form.commission_percent} onChange={(e) => setForm({ ...form, commission_percent: e.target.value })} placeholder="0" data-testid="inc-comm-pct" /></SoftField>
              <SoftField label="Commission Type">
                <Select value={form.commission_type} onValueChange={(v) => setForm({ ...form, commission_type: v })}>
                  <SelectTrigger data-testid="inc-comm-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{COMMISSION_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </SoftField>
              <SoftField label="Commission Amount (auto)"><Input value={INR(calc.comm_amount)} readOnly className="bg-slate-50 dark:bg-slate-900 mono" /></SoftField>
            </div>
          </SectionCard>

          <SectionCard title="Incentive Requested">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SoftField label="Incentive % Demanded *"><Input type="number" value={form.incentive_demanded_percent} onChange={(e) => setForm({ ...form, incentive_demanded_percent: e.target.value })} placeholder="0" data-testid="inc-demanded" /></SoftField>
              <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 px-3 py-2 text-[11.5px] text-slate-600 dark:text-slate-400">
                <div className="font-semibold text-slate-700 dark:text-slate-300 mb-0.5">Incentive % Given</div>
                <div>Set by your assigned Manager / Master Admin during review.</div>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Team Contribution (optional)">
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Did someone else help you close this order?</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="inc-helpers">
              {people.map((p) => {
                const active = form.helper_user_ids.includes(p.id);
                return (
                  <button key={p.id} type="button" onClick={() => toggleHelper(p)}
                    className={`px-3 py-1.5 text-[12px] rounded-full border-2 transition-all ${active ? "!bg-indigo-600 !border-indigo-700 !text-white shadow-sm font-semibold" : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200 hover:border-indigo-300 dark:hover:border-indigo-500/50"}`}
                    data-testid={`inc-helper-${p.id}`} data-keep-color={active ? "" : undefined}>
                    {active && <CheckCircle2 size={11} className="inline mr-1 -mt-0.5" strokeWidth={2.5} />}{p.name}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md flex items-center gap-3">
              <Switch checked={form.share_incentive} onCheckedChange={setShareIncentive} data-testid="inc-share-toggle" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Share my incentive with someone who helped?</span>
            </div>

            {form.share_incentive && (
              <div className="mt-3 space-y-2">
                {form.shared_with.length === 0 && <div className="text-[11px] text-slate-500 dark:text-slate-400 italic">Add one or more recipients below.</div>}
                {form.shared_with.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`inc-share-row-${i}`}>
                    <div className="col-span-7">
                      <Select value={s.user_id} onValueChange={(v) => {
                        const p = people.find((x) => x.id === v);
                        updateShareRow(i, { user_id: v, user_name: p?.name || "" });
                      }}>
                        <SelectTrigger data-testid={`inc-share-user-${i}`}><SelectValue placeholder="Select team mate…" /></SelectTrigger>
                        <SelectContent>{people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-4"><Input type="number" value={s.percent} onChange={(e) => updateShareRow(i, { percent: e.target.value })} placeholder="% share" data-testid={`inc-share-pct-${i}`} /></div>
                    <div className="col-span-1">
                      <Button variant="outline" size="sm" onClick={() => removeShareRow(i)} className="h-9 w-9 p-0 text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-900/30" data-testid={`inc-share-rm-${i}`}><Trash2 size={12} /></Button>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between">
                  <Button variant="outline" size="sm" onClick={addShareRow} className="h-8 text-xs" data-testid="inc-share-add"><Plus size={11} className="mr-1" /> Add Recipient</Button>
                  <div className={`text-[11px] font-semibold mono ${sharedSum > 100 ? "text-rose-600" : "text-slate-600 dark:text-slate-400"}`}>Total share: {sharedSum.toFixed(1)}% {sharedSum > 100 && "⚠ exceeds 100"}</div>
                </div>
              </div>
            )}
          </SectionCard>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="!bg-indigo-600 hover:!bg-indigo-700 !text-white" data-testid="inc-submit" data-keep-color>
            {saving ? "Submitting…" : "Submit Incentive Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==== View Submission Dialog (read-only detail) ==== */

function ViewSubmissionDialog({ submission: s, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0" data-testid="inc-view-dialog">
        <div className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-white dark:bg-slate-950 ring-1 ring-slate-200 dark:ring-slate-800 flex items-center justify-center">
              <Award size={20} className="text-slate-700 dark:text-slate-200" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="heading text-base font-bold text-slate-900 dark:text-slate-100 truncate">{s.client_name}</DialogTitle>
              <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">by {s.created_by_name} · {fmtDateTime(s.created_at)}</div>
            </div>
            <StageBadge stage={s.stage} status={s.status} hasAssignedManager={s.has_assigned_manager} />
          </div>
        </div>
        <DialogHeader className="hidden"><DialogTitle>View</DialogTitle></DialogHeader>

        <div className="px-6 py-4 space-y-4">
          {/* Order + commission summary */}
          <SectionCard title="Order & Commission">
            {(s.order_no || s.mode === "auto") && (
              <div className="mb-2 flex items-center gap-2 text-[11.5px] flex-wrap" data-testid="inc-view-order-link">
                <Badge className={`${s.mode === "auto" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"} text-[10px] font-semibold`} data-keep-color>
                  {s.mode === "auto" ? "Auto (linked)" : "Manual"}
                </Badge>
                {s.order_no && <span className="text-slate-700 dark:text-slate-200 font-semibold mono">Order {s.order_no}</span>}
                {s.invoice_ids?.length ? <span className="text-slate-500 dark:text-slate-400">· {s.invoice_ids.length} invoice{s.invoice_ids.length === 1 ? "" : "s"}</span> : null}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <KV k="Before GST" v={INR(s.before_gst_amount)} />
              <KV k="GST" v={`${s.gst_percent}% (${INR(s.gst_amount)})`} />
              <KV k="Total Order" v={INR(s.total_order_value)} />
              <KV k="Commission" v={`${s.commission_percent}% · ${s.commission_type}`} />
              <KV k="Commission Amount" v={INR(s.commission_amount)} span />
            </div>
          </SectionCard>

          {/* Incentive */}
          <SectionCard title="Incentive">
            <div className="grid grid-cols-2 gap-2">
              <KV k="Demanded %" v={`${s.incentive_demanded_percent}%`} />
              <KV k="Given %" v={s.incentive_given_percent ? `${s.incentive_given_percent}%` : "Pending"} />
              <KV k="Payable" v={s.stage === "manager_review" || s.stage === "admin_approval" ? "Pending" : INR(s.incentive_amount)} span />
              <KV k="Yearly Target Hit" v={s.completed_yearly_target ? "Yes" : "No"} />
              <KV k="Yearly Target" v={INR(s.yearly_target_snapshot)} />
              <KV k="FY Sales Done" v={`${s.fy_label_snapshot || ""} · ${INR(s.fy_sales_done_snapshot)}`} span />
            </div>
          </SectionCard>

          {/* v106c — waiting-for-final-value banner between Manager approval and Master Admin approval */}
          {s.stage === "admin_approval" && (
            <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10 px-3 py-2.5 text-[12px] text-amber-800 dark:text-amber-200 flex items-start gap-2" data-testid="inc-view-awaiting-final">
              <Clock size={14} className="flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Pre-Approved — waiting on final incentive.</div>
                <div className="text-[11px] mt-0.5">The order&apos;s profit will be calculated and the final incentive will be marked automatically. Sit tight — we&apos;re handling the math for you.</div>
              </div>
            </div>
          )}

          {/* Decision trail */}
          {(s.manager_decision || s.admin_decision) && (
            <SectionCard title="Decision Trail">
              {s.manager_decision && (
                <div className={`rounded-md border px-3 py-2 mb-2 ${s.manager_decision === "approved" ? "border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10" : "border-rose-200 bg-rose-50 dark:border-rose-500/30 dark:bg-rose-500/10"}`}>
                  <div className="text-[11.5px] font-bold text-slate-800 dark:text-slate-200">
                    Manager {s.manager_decision === "approved" ? "approved" : "rejected"}
                    {s.manager_decided_by_name ? ` by ${s.manager_decided_by_name}` : ""} · {fmtDateTime(s.manager_decided_at)}
                  </div>
                  {s.manager_reason && <div className="text-[11px] text-slate-700 dark:text-slate-300 italic mt-1">“{s.manager_reason}”</div>}
                </div>
              )}
              {s.admin_decision && (
                <div className={`rounded-md border px-3 py-2 ${s.admin_decision === "approved" ? "border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10" : "border-rose-200 bg-rose-50 dark:border-rose-500/30 dark:bg-rose-500/10"}`}>
                  <div className="text-[11.5px] font-bold text-slate-800 dark:text-slate-200">
                    Master Admin {s.admin_decision === "approved" ? "approved" : "rejected"}
                    {s.admin_decided_by_name ? ` by ${s.admin_decided_by_name}` : ""} · {fmtDateTime(s.admin_decided_at)}
                  </div>
                  {s.admin_reason && <div className="text-[11px] text-slate-700 dark:text-slate-300 italic mt-1">“{s.admin_reason}”</div>}
                </div>
              )}
            </SectionCard>
          )}

          {/* Payment */}
          {s.stage === "paid" && (
            <SectionCard title="Payment">
              <div className="grid grid-cols-2 gap-2">
                <KV k="Mode" v={s.payment_mode || "—"} />
                <KV k="Reference" v={s.payment_reference || "—"} />
                <KV k="Disbursed On" v={s.payment_disbursed_on || "—"} />
                <KV k="Disbursed By" v={s.payment_disbursed_by_name || "—"} />
                {s.payment_remarks && <KV k="Remarks" v={s.payment_remarks} span />}
              </div>
            </SectionCard>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex justify-end">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==== Reassign Manager Dialog (MA only) ==== */

function ReassignManagerDialog({ submission, reviewerOptions, onClose, onSaved }) {
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const isUnassigned = !submission.assigned_manager_id;

  const options = reviewerOptions.filter((r) => r.id !== submission.created_by_id);

  const save = async () => {
    if (!pick) { toast.error("Please pick a manager"); return; }
    setBusy(true);
    try {
      await api.post(`/incentive/submissions/${submission.id}/assign-manager`, { assigned_manager_id: pick });
      toast.success(isUnassigned ? "Manager assigned" : "Manager reassigned");
      onSaved && onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="inc-reassign-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2"><UserCog size={16} /> {isUnassigned ? "Assign Manager for Incentive" : "Reassign Manager for Incentive"}</DialogTitle>
          <div className="text-[11.5px] text-slate-500 dark:text-slate-400">
            {isUnassigned
              ? <>Submission from <span className="font-semibold text-slate-700 dark:text-slate-200">{submission.created_by_name}</span> is waiting for a reviewer.</>
              : <>Currently assigned to <span className="font-semibold text-slate-700 dark:text-slate-200">{submission.assigned_manager_name || "—"}</span>. Pick a different reviewer below.</>}
          </div>
        </DialogHeader>
        <SoftField label={isUnassigned ? "Pick Manager for Incentive" : "New Manager for Incentive"}>
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger data-testid="inc-reassign-pick"><SelectValue placeholder="Select reviewer…" /></SelectTrigger>
            <SelectContent>
              {options.length === 0 && <div className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400 italic">No eligible reviewers. Ask Master Admin to add someone under Tab Access → Review By Manager.</div>}
              {options.map((r) => (
                <SelectItem key={r.id} value={r.id} data-testid={`inc-reassign-opt-${r.id}`}>{r.name}{r.role ? ` · ${r.role}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SoftField>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="!bg-indigo-600 hover:!bg-indigo-700 !text-white" data-testid="inc-reassign-save" data-keep-color>
            {busy ? "Saving…" : (isUnassigned ? "Assign" : "Reassign")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==== Manager & Admin Decision Dialogs (unchanged flow, reviewer sets %) ==== */

function ManagerDecisionDialog({ submission, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const act = async (decision) => {
    if (!reason.trim()) { toast.error("Reason is mandatory"); return; }
    setBusy(true);
    try {
      await api.post(`/incentive/submissions/${submission.id}/manager-decision`, {
        decision, reason,
      });
      toast.success(`Submission ${decision}`);
      onSaved && onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto p-0" data-testid="inc-mgr-dialog">
        <div className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-white dark:bg-slate-950 ring-1 ring-slate-200 dark:ring-slate-800 flex items-center justify-center"><Users size={20} className="text-slate-700 dark:text-slate-200" /></div>
            <div>
              <DialogTitle className="heading text-base font-bold text-slate-900 dark:text-slate-100">Manager Review · {submission.client_name}</DialogTitle>
              <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">By {submission.created_by_name} · {fmtDateTime(submission.created_at)}</div>
            </div>
          </div>
        </div>
        <DialogHeader className="hidden"><DialogTitle>Manager Review</DialogTitle></DialogHeader>

        <div className="px-6 py-4 space-y-4">
          <SubmissionSnapshot s={submission} />
          <div className="text-[11.5px] text-slate-600 dark:text-slate-300 px-3 py-2 rounded-md bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            You are approving the <span className="font-semibold">legitimacy</span> of this incentive request only. The final <span className="font-semibold">Incentive % Given</span> and payable amount will be set by Master Admin after profit calculation on the order.
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Reason *</Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5" placeholder="Justification for approve/reject…" data-testid="inc-mgr-reason" />
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => act("rejected")} disabled={busy} className="!bg-rose-600 hover:!bg-rose-700 !text-white" data-testid="inc-mgr-reject" data-keep-color><XCircle size={14} className="mr-1.5" /> Reject</Button>
          <Button onClick={() => act("approved")} disabled={busy} className="!bg-emerald-600 hover:!bg-emerald-700 !text-white" data-testid="inc-mgr-approve" data-keep-color><CheckCircle2 size={14} className="mr-1.5" /> Approve &amp; Forward</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminDecisionDialog({ submission, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [givenPct, setGivenPct] = useState(String(submission.incentive_given_percent || submission.incentive_demanded_percent || 0));
  const [busy, setBusy] = useState(false);

  const newAmount = useMemo(() => {
    const pct = parseFloat(givenPct) || 0;
    return +(((submission.commission_amount || 0) * pct) / 100).toFixed(2);
  }, [givenPct, submission.commission_amount]);

  const act = async (decision) => {
    if (!reason.trim()) { toast.error("Reason is mandatory"); return; }
    setBusy(true);
    try {
      await api.post(`/incentive/submissions/${submission.id}/admin-decision`, {
        decision, reason, incentive_given_percent: parseFloat(givenPct) || 0,
      });
      toast.success(`Submission ${decision}`);
      onSaved && onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto p-0" data-testid="inc-adm-dialog">
        <div className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-white dark:bg-slate-950 ring-1 ring-slate-200 dark:ring-slate-800 flex items-center justify-center"><Trophy size={20} className="text-slate-700 dark:text-slate-200" /></div>
            <div>
              <DialogTitle className="heading text-base font-bold text-slate-900 dark:text-slate-100">Admin Approval · {submission.client_name}</DialogTitle>
              <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">By {submission.created_by_name} · {fmtDateTime(submission.created_at)}</div>
            </div>
          </div>
        </div>
        <DialogHeader className="hidden"><DialogTitle>Admin Approval</DialogTitle></DialogHeader>

        <div className="px-6 py-4 space-y-4">
          <SubmissionSnapshot s={submission} />
          {submission.manager_decided_by_name && (
            <div className="text-[11px] px-3 py-2 rounded-md bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-800 dark:text-emerald-300">
              <span className="font-semibold">Manager Approved:</span> {submission.manager_decided_by_name} · {fmtDateTime(submission.manager_decided_at)}
              {submission.manager_reason && <div className="italic mt-1">“{submission.manager_reason}”</div>}
            </div>
          )}
          <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-3">
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Incentive % Given *</Label>
            <div className="flex items-center gap-3 mt-1.5">
              <Input type="number" value={givenPct} onChange={(e) => setGivenPct(e.target.value)} className="w-32 bg-white dark:bg-slate-950" data-testid="inc-adm-given" />
              <span className="text-[12px] text-slate-600 dark:text-slate-400">Payable: <span className="font-bold mono text-slate-900 dark:text-slate-100">{INR(newAmount)}</span></span>
            </div>
            <div className="text-[10.5px] text-slate-500 dark:text-slate-400 mt-1">Manager set this to {submission.incentive_given_percent}%. Adjust before final approval.</div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Reason *</Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5" placeholder="Reason for final decision…" data-testid="inc-adm-reason" />
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => act("rejected")} disabled={busy} className="!bg-rose-600 hover:!bg-rose-700 !text-white" data-testid="inc-adm-reject" data-keep-color><XCircle size={14} className="mr-1.5" /> Reject</Button>
          <Button onClick={() => act("approved")} disabled={busy} className="!bg-indigo-600 hover:!bg-indigo-700 !text-white" data-testid="inc-adm-approve" data-keep-color><CheckCircle2 size={14} className="mr-1.5" /> Approve &amp; Send to Payment Desk</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarkPaidDialog({ submission, onClose, onSaved }) {
  const [form, setForm] = useState({
    payment_mode: submission.payment_mode || "",
    payment_reference: submission.payment_reference || "",
    payment_disbursed_on: submission.payment_disbursed_on || new Date().toISOString().slice(0, 10),
    payment_remarks: submission.payment_remarks || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.payment_mode.trim()) { toast.error("Payment mode is required"); return; }
    setBusy(true);
    try {
      await api.post(`/incentive/submissions/${submission.id}/mark-paid`, form);
      toast.success("Marked as paid");
      onSaved && onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto p-0" data-testid="inc-pay-dialog">
        <div className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-white dark:bg-slate-950 ring-1 ring-slate-200 dark:ring-slate-800 flex items-center justify-center"><CreditCard size={20} className="text-slate-700 dark:text-slate-200" /></div>
            <div>
              <DialogTitle className="heading text-base font-bold text-slate-900 dark:text-slate-100">Disburse · {submission.client_name}</DialogTitle>
              <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">To {submission.created_by_name} · {INR(submission.incentive_amount)}</div>
            </div>
          </div>
        </div>
        <DialogHeader className="hidden"><DialogTitle>Disburse</DialogTitle></DialogHeader>

        <div className="px-6 py-4 space-y-3">
          <SoftField label="Payment Mode *">
            <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
              <SelectTrigger data-testid="inc-pay-mode"><SelectValue placeholder="Select mode…" /></SelectTrigger>
              <SelectContent>{["Bank Transfer", "UPI", "Cash", "Cheque", "Salary Add-on", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </SoftField>
          <SoftField label="Reference / Transaction ID"><Input value={form.payment_reference} onChange={(e) => setForm({ ...form, payment_reference: e.target.value })} placeholder="UTR / Cheque no. / …" data-testid="inc-pay-ref" /></SoftField>
          <SoftField label="Disbursed On *"><Input type="date" value={form.payment_disbursed_on} onChange={(e) => setForm({ ...form, payment_disbursed_on: e.target.value })} data-testid="inc-pay-date" /></SoftField>
          <SoftField label="Remarks"><Textarea rows={2} value={form.payment_remarks} onChange={(e) => setForm({ ...form, payment_remarks: e.target.value })} placeholder="Optional…" data-testid="inc-pay-remarks" /></SoftField>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={busy} className="!bg-sky-600 hover:!bg-sky-700 !text-white" data-testid="inc-pay-save" data-keep-color><CreditCard size={14} className="mr-1.5" /> {busy ? "Saving…" : "Record Payment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==== Shared helpers ==== */

function SubmissionSnapshot({ s }) {
  return (
    <div className="grid grid-cols-2 gap-2 text-[12px]" data-testid="inc-snapshot">
      <KV k="Order Value" v={INR(s.total_order_value)} />
      <KV k="Commission" v={`${s.commission_percent}% (${s.commission_type}) = ${INR(s.commission_amount)}`} />
      <KV k="Incentive Demanded" v={`${s.incentive_demanded_percent}%`} />
      <KV k="Target Hit" v={s.completed_yearly_target ? "Yes" : "No"} />
      {s.helper_user_names?.length > 0 && <KV k="Helpers" v={s.helper_user_names.join(", ")} span />}
      {s.shared_with?.length > 0 && <KV k="Shared With" v={s.shared_with.map((x) => `${x.user_name} (${x.percent}%)`).join(", ")} span />}
    </div>
  );
}

function KV({ k, v, span = false }) {
  return (
    <div className={`flex items-center justify-between px-2.5 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md ${span ? "col-span-2 md:col-span-full" : ""}`}>
      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{k}</span>
      <span className="font-bold text-slate-900 dark:text-slate-100 mono text-[12px] text-right">{v}</span>
    </div>
  );
}

function EmptyState({ title, hint }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 py-12 text-center" data-testid="inc-empty">
      <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
        <Inbox size={20} className="text-slate-400 dark:text-slate-500" />
      </div>
      <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">{title}</div>
      {hint && <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

function MiniStat({ label, value, highlight = false }) {
  return (
    <div className={`px-2 py-1.5 rounded-md border border-slate-200 dark:border-slate-800 ${highlight ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900" : "bg-slate-50 dark:bg-slate-900"}`} data-keep-color={highlight ? "" : undefined}>
      <div className={`text-[9px] uppercase tracking-[0.14em] font-bold ${highlight ? "text-slate-300 dark:text-slate-600" : "text-slate-500 dark:text-slate-400"}`}>{label}</div>
      <div className={`text-[12px] font-bold mono ${highlight ? "text-white dark:text-slate-900" : "text-slate-900 dark:text-slate-100"}`}>{value}</div>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th className={`px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500 dark:text-slate-400 text-${align}`}>{children}</th>;
}

function Td({ children, align, mono = false, className = "", ...rest }) {
  return <td className={`px-4 py-3 ${mono ? "mono" : ""} ${align === "right" ? "text-right" : ""} ${className}`} {...rest}>{children}</td>;
}

function SoftField({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4">
      <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 dark:text-slate-400 mb-3">{title}</div>
      {children}
    </div>
  );
}
